import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllCrusts,
  getSingleCrustById,
  createCrust,
  updateCrust,
  deleteCrust,
} from "@/api/new-api/staff-management/menu-manage/essential-menu/crust";

// Thunks

export const fetchAllCrusts = createAsyncThunk(
  "crusts/fetchAll",
  async ({ restaurant_id, menu_id }, { rejectWithValue }) => {
    const res = await getAllCrusts(restaurant_id, menu_id);
    if (res.status === "success") return res.data.crusts;
    return rejectWithValue(res.message);
  }
);

export const fetchSingleCrust = createAsyncThunk(
  "crusts/fetchOne",
  async (id, { rejectWithValue }) => {
    const res = await getSingleCrustById(id);
    if (res.status === "success") return res.data.crust;
    return rejectWithValue(res.message);
  }
);

export const createNewCrust = createAsyncThunk(
  "crusts/create",
  async (data, { rejectWithValue }) => {
    const res = await createCrust(data);
    if (res.status === "success") return res.data.crust;
    return rejectWithValue(res.message);
  }
);

export const updateExistingCrust = createAsyncThunk(
  "crusts/update",
  async ({ id, data }, { rejectWithValue }) => {
    const res = await updateCrust(id, data);
    if (res.status === "success") return res.data.crust;
    return rejectWithValue(res.message);
  }
);

export const deleteCrustById = createAsyncThunk(
  "crusts/delete",
  async (id, { rejectWithValue }) => {
    const res = await deleteCrust(id);
    if (res.status === "success") return id;
    return rejectWithValue(res.message);
  }
);

// Initial State

const initialState = {
  crusts: [],
  singleCrust: null,

  getLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,

  success: null,
  error: null,
  requestStatus: false,
};

// Slice

const crustSlice = createSlice({
  name: "crusts",
  initialState,
  reducers: {
    clearCrustMessages: (state) => {
      state.success = null;
      state.error = null;
      state.requestStatus = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All
      .addCase(fetchAllCrusts.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchAllCrusts.fulfilled, (state, action) => {
        state.getLoading = false;
        state.crusts = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchAllCrusts.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Fetch Single
      .addCase(fetchSingleCrust.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchSingleCrust.fulfilled, (state, action) => {
        state.getLoading = false;
        state.singleCrust = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchSingleCrust.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Create
      .addCase(createNewCrust.pending, (state) => {
        state.createLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(createNewCrust.fulfilled, (state, action) => {
        state.createLoading = false;
        state.crusts.push(action.payload);
        state.success = "Crust created successfully!";
        state.requestStatus = true;
      })
      .addCase(createNewCrust.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Update
      .addCase(updateExistingCrust.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(updateExistingCrust.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.crusts = state.crusts.map((crust) =>
          crust.id === action.payload.id ? action.payload : crust
        );
        state.success = "Crust updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateExistingCrust.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Delete
      .addCase(deleteCrustById.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(deleteCrustById.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.crusts = state.crusts.map((item) =>
          item.id === action.payload ? { ...item, status: "archive" } : item
        );
        state.success = "Crust deleted successfully!";
        state.requestStatus = true;
      })
      .addCase(deleteCrustById.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      });
  },
});

export const { clearCrustMessages } = crustSlice.actions;
export default crustSlice.reducer;
